// components/AlbumComponent.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./AlbumComponent.module.css";

/**
 * 전체 화면 오버레이 앨범 (Portal로 document.body에 렌더)
 *
 * props:
 *  - open: boolean
 *  - items: { src: string, title?: string, description?: string }[]
 *  - startIndex?: number
 *  - onClose?: () => void
 *  - onIndexChange?: (i:number) => void
 *  - preloadRadius?: number
 *  - marginTop?: number         // 닫기 버튼 top 오프셋
 *  - peekPx?: number            // 양 옆에 살짝 보이게 할 폭(px). 기본 48
 *  - gapPx?: number             // 슬라이드 간 간격(px). 기본 24
 */
export default function AlbumComponent({
  open,
  items,
  startIndex = 0,
  onClose,
  onIndexChange,
  preloadRadius = 2,
  marginTop,
  peekPx = 48,
  gapPx = 24,
}) {
  const trackRef = useRef(null);
  const [idx, setIdx] = useState(startIndex);

  // open 시 CSS 변수 적용 + 초기 위치 스냅
  useEffect(() => {
    if (!open) return;
    const el = trackRef.current;
    if (!el) return;

    // CSS 변수 주입
    el.style.setProperty("--peek", `${peekPx}px`);
    el.style.setProperty("--gap", `${gapPx}px`);
    el.style.setProperty("--cardW", `calc(100vw - (${peekPx}px * 2))`);
    el.style.setProperty("--spacerW", `calc(${peekPx}px + (${gapPx}px / 2))`);

    // 처음 위치로 이동 (브라우저 표준 스냅 사용)
    requestAnimationFrame(() => {
      const slides = el.querySelectorAll(`.${styles.slide}`);
      const target = slides[startIndex];
      if (target?.scrollIntoView) {
        target.scrollIntoView({
          behavior: "auto",
          inline: "center",
          block: "nearest",
        });
      }
      setIdx(startIndex);
    });
  }, [open, startIndex, peekPx, gapPx, items?.length]);

  // 바디 스크롤 락
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // ESC/키보드 내비
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, idx, items?.length]);

  // 중앙 카드 판정: 트랙 중앙 vs 각 슬라이드 중앙 거리 비교
  useEffect(() => {
    if (!open) return;
    const el = trackRef.current;
    if (!el) return;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const trackRect = el.getBoundingClientRect();
        const centerX = trackRect.left + trackRect.width / 2;

        let nearest = 0;
        let best = Infinity;
        const slides = el.querySelectorAll(`.${styles.slide}`);
        slides.forEach((s, i) => {
          const r = s.getBoundingClientRect();
          const slideCenter = r.left + r.width / 2;
          const d = Math.abs(slideCenter - centerX);
          if (d < best) { best = d; nearest = i; }
        });

        if (nearest !== idx) {
          setIdx(nearest);
          onIndexChange?.(nearest);
        }
        ticking = false;
      });
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, idx, onIndexChange]);

  // 프리로드
  const eagerSet = useMemo(() => {
    const set = new Set();
    const n = items?.length || 0;
    for (let i = Math.max(0, idx - preloadRadius); i <= Math.min(n - 1, idx + preloadRadius); i++) {
      set.add(i);
    }
    return set;
  }, [idx, items, preloadRadius]);

  useEffect(() => {
    if (!open) return;
    eagerSet.forEach((i) => {
      const src = items?.[i]?.src;
      if (!src) return;
      const im = new Image();
      im.decoding = "async";
      im.loading = "eager";
      im.src = src;
    });
  }, [open, eagerSet, items]);

  const scrollToIndex = (i, { instant = false } = {}) => {
    const el = trackRef.current;
    if (!el) return;
    const slides = el.querySelectorAll(`.${styles.slide}`);
    const target = slides[i];
    if (!target?.scrollIntoView) return;
    target.scrollIntoView({
      behavior: instant ? "auto" : "smooth",
      inline: "center",
      block: "nearest",
    });
  };

  const go = (delta) => {
    const n = items?.length || 0;
    const next = Math.max(0, Math.min(n - 1, idx + delta));
    scrollToIndex(next);
  };

  if (!open) return null;

  const overlay = (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      onClick={()=>onClose?.()}
      onTouchStart={e=>e.stopPropagation()}
    >
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <img
          src="images/close_line.svg"
          alt="close"
          className={styles.closeIcon}
          style={{ top: `${(marginTop ?? 0) + 8}px` }}
          onClick={() => onClose?.()}
        />

        {/* 슬라이드 트랙 */}
        <div
          className={styles.track}
          onClick={() => onClose?.()}
          ref={trackRef}
          style={{
            "--peek": `${peekPx}px`,
            "--gap": `${gapPx}px`,
            "--cardW": `calc(100vw - (${peekPx}px * 2))`,
            "--spacerW": `calc(${peekPx}px + (${gapPx}px / 2))`,
          }}
        >
          {/* 앞 스페이서: 첫 카드 중앙 정렬용 */}
          <div className={styles.spacer} aria-hidden />

          {items?.map((item, i) => {
            const eager = eagerSet.has(i);
            return (
              <div className={styles.slide} key={`${item.src}_${i}`}>
                {/* 카드: 폭을 콘텐츠(이미지) 폭에 맞추는 컨테이너 */}
                <div className={styles.card} onClick={(e)=>{e.stopPropagation()}} >
                  <img
                    className={styles.img}
                    src={item.src}
                    alt={item.title || ""}
                    decoding="async"
                    loading={eager ? "eager" : "lazy"}
                    draggable="false"
                  />
                  {(item.title || item.description) && (
                    <div className={styles.caption}>
                      {item.title ? <div className={styles.title}>{item.title}</div> : null}
                      {item.description ? <div className={styles.desc}>{item.description}</div> : null}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* 뒤 스페이서: 마지막 카드 중앙 정렬용 */}
          <div className={styles.spacer} aria-hidden />
        </div>

        {/* 하단 인디케이터 */}
        {(items?.length || 0) > 1 && (
          <div className={styles.indicatorWrap}>
            {items.length <= 15 ? (
              <div className={styles.dots}>
                {items.map((_, i) => (
                  <span
                    key={i}
                    className={`${styles.dot} ${i === idx ? styles.active : ""}`}
                  />
                ))}
              </div>
            ) : (
              <div className={styles.pageIndicator}>
                {idx + 1} / {items.length}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
